import React, { useState } from "react";
import { Alert, Box, Button, CircularProgress, Grid, MenuItem, TextField, Typography } from "@mui/material";
import { actualizarTraspasoDinero, crearTraspasoDinero, fmtFecha, fmtMoney, listarTraspasosDinero, tipoCambioDe } from "../services/egresosService";
import { fieldSx } from "./formStyles";
import { ComprobanteMarco } from "./ComprobanteUi";
import { SUB_COLORES } from "./coloresSub";
import ListadoTabla from "./ListadoTabla";
import usePaginado from "./usePaginado";
import EditarRegistroDialog, { columnaEditar } from "./EditarRegistroDialog";

const hoy = () => new Date().toISOString().slice(0, 10);
const Campo = ({ children, size = { xs: 12, sm: 6, md: 3 } }) => <Grid size={size}>{children}</Grid>;

const inicial = (catalogos) => ({
  banco_origen: catalogos.bancos[0], comprobante_origen: "", num_cheque_origen: "",
  banco_destino: catalogos.bancos[1] || catalogos.bancos[0], comprobante_destino: "", num_cheque_destino: "",
  monto: "", moneda: "Bolivianos", tipo_cambio: catalogos.cambio ?? "", fecha: hoy(), obs: "",
});

// Formulario a partir de un traspaso guardado (el monto es el de su moneda; el tipo de cambio sale de Bs / USD).
const desdeFila = (r) => ({
  banco_origen: r.banco_origen || "", comprobante_origen: r.comprobante_origen || "", num_cheque_origen: r.num_cheque_origen || "",
  banco_destino: r.banco_destino || "", comprobante_destino: r.comprobante_destino || "", num_cheque_destino: r.num_cheque_destino || "",
  monto: String(r.moneda === "Dolares" ? r.dolares : r.bolivianos), moneda: r.moneda === "Dolares" ? "Dolares" : "Bolivianos",
  tipo_cambio: tipoCambioDe(r.bolivianos, r.dolares), fecha: fmtFecha(r.fecha_registro), obs: r.obs || "",
});

// Campos del traspaso, compartidos por el formulario de registro y el diálogo de edición.
// `bloqueado`: traspaso conciliado, no se tocan bancos, monto, moneda, tipo de cambio ni fecha.
function CamposTraspaso({ form, setForm, bancos, bloqueado = false }) {
  const set = (campo) => (e) => setForm((f) => ({ ...f, [campo]: e.target.value }));
  const opciones = (actual) => (actual && !bancos.includes(actual) ? [actual, ...bancos] : bancos);
  const banco = (campo, label) => (
    <TextField select fullWidth size="small" label={label} value={form[campo]} onChange={set(campo)} disabled={bloqueado} sx={fieldSx}>
      {opciones(form[campo]).map((b) => <MenuItem key={b} value={b}>{b}</MenuItem>)}
    </TextField>
  );

  return (
    <>
      <Grid size={{ xs: 12 }}><Typography sx={{ fontSize: 12, fontWeight: 800 }} color="text.secondary">Origen</Typography></Grid>
      <Campo>{banco("banco_origen", "Banco origen")}</Campo>
      <Campo><TextField fullWidth size="small" label="Comprobante origen" value={form.comprobante_origen} onChange={set("comprobante_origen")} sx={fieldSx} /></Campo>
      <Campo><TextField fullWidth size="small" label="N° cheque origen" value={form.num_cheque_origen} onChange={set("num_cheque_origen")} sx={fieldSx} /></Campo>

      <Grid size={{ xs: 12 }}><Typography sx={{ fontSize: 12, fontWeight: 800 }} color="text.secondary">Destino</Typography></Grid>
      <Campo>{banco("banco_destino", "Banco destino")}</Campo>
      <Campo><TextField fullWidth size="small" label="Comprobante destino" value={form.comprobante_destino} onChange={set("comprobante_destino")} sx={fieldSx} /></Campo>
      <Campo><TextField fullWidth size="small" label="N° cheque destino" value={form.num_cheque_destino} onChange={set("num_cheque_destino")} sx={fieldSx} /></Campo>

      <Campo size={{ xs: 6, md: 2 }}><TextField fullWidth size="small" label="Monto" value={form.monto} onChange={set("monto")} disabled={bloqueado} sx={fieldSx} /></Campo>
      <Campo size={{ xs: 6, md: 2 }}>
        <TextField select fullWidth size="small" label="Moneda" value={form.moneda} onChange={set("moneda")} disabled={bloqueado} sx={fieldSx}>
          <MenuItem value="Bolivianos">Bolivianos</MenuItem>
          <MenuItem value="Dolares">Dólares</MenuItem>
        </TextField>
      </Campo>
      <Campo size={{ xs: 6, md: 2 }}><TextField fullWidth size="small" label="Tipo de cambio" value={form.tipo_cambio} onChange={set("tipo_cambio")} disabled={bloqueado} sx={fieldSx} /></Campo>
      <Campo size={{ xs: 6, md: 2 }}><TextField fullWidth size="small" type="date" label="Fecha" value={form.fecha} onChange={set("fecha")} disabled={bloqueado} slotProps={{ inputLabel: { shrink: true } }} sx={fieldSx} /></Campo>
      <Campo size={{ xs: 12, md: 4 }}><TextField fullWidth size="small" label="Observación" value={form.obs} onChange={set("obs")} sx={fieldSx} /></Campo>
    </>
  );
}

function EditarTraspasoDialog({ fila, bancos, onClose, onGuardado }) {
  const [form, setForm] = useState(() => desdeFila(fila));
  const conciliado = !!Number(fila.conciliado);
  return (
    <EditarRegistroDialog
      titulo="Editar traspaso de dinero" id={fila.id} conciliado={conciliado} bloqueadosTxt="los bancos, el monto, la moneda y la fecha"
      onClose={onClose} onGuardar={async () => { await actualizarTraspasoDinero(fila.id, form); onGuardado(); }}
    >
      <Grid container spacing={0.6}><CamposTraspaso form={form} setForm={setForm} bancos={bancos} bloqueado={conciliado} /></Grid>
    </EditarRegistroDialog>
  );
}

// Traspaso de dinero entre bancos (espejo de guarda_traspaso.php → tabla traspaso_interno).
export default function TraspasoDineroForm({ catalogos }) {
  const [form, setForm] = useState(() => inicial(catalogos));
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");
  const [ok, setOk] = useState("");
  const [editando, setEditando] = useState(null);
  const paginado = usePaginado((q) => listarTraspasosDinero(q));

  const guardar = async () => {
    setGuardando(true);
    setError("");
    setOk("");
    try {
      await crearTraspasoDinero(form);
      setOk("Traspaso guardado correctamente.");
      setForm(inicial(catalogos));
      paginado.recargar(true);
    } catch (e) {
      setError(e?.response?.data?.error || "No se pudo guardar el traspaso.");
    } finally {
      setGuardando(false);
    }
  };

  return (
    <>
    <ComprobanteMarco titulo="Traspaso de dinero" color={SUB_COLORES[1]}>
      {error && <Alert severity="error" sx={{ mb: 1, py: 0 }}>{error}</Alert>}
      {ok && <Alert severity="success" sx={{ mb: 1, py: 0 }}>{ok}</Alert>}
      <Grid container spacing={0.6}>
        <CamposTraspaso form={form} setForm={setForm} bancos={catalogos.bancos} />

        <Grid size={{ xs: 12 }} sx={{ display: "flex", gap: 1 }}>
          <Box sx={{ flexGrow: 1 }} />
          <Button size="small" variant="outlined" color="inherit" sx={{ borderRadius: 0 }} onClick={() => { setForm(inicial(catalogos)); setError(""); setOk(""); }}>Limpiar</Button>
          <Button size="small" variant="contained" sx={{ borderRadius: 0 }} onClick={guardar} disabled={guardando}>
            {guardando ? <CircularProgress size={16} color="inherit" /> : "Guardar"}
          </Button>
        </Grid>
      </Grid>

    </ComprobanteMarco>
    <ListadoTabla color={SUB_COLORES[1]}
      titulo="Traspasos de dinero registrados" filas={paginado.items} cargando={paginado.cargando} paginado={paginado} vacio="No existen traspasos registrados."
      columnas={[
        { h: "ID", render: (r) => r.id, sx: { fontWeight: 800, color: SUB_COLORES[1] } },
        { h: "Banco origen", render: (r) => r.banco_origen }, { h: "Comp. origen", render: (r) => r.comprobante_origen },
        { h: "Cheque origen", render: (r) => r.num_cheque_origen }, { h: "Banco destino", render: (r) => r.banco_destino },
        { h: "Comp. destino", render: (r) => r.comprobante_destino }, { h: "Cheque destino", render: (r) => r.num_cheque_destino },
        { h: "Dólares", align: "right", render: (r) => fmtMoney(r.dolares), sx: { fontWeight: 700, color: "#2e7d32" } },
        { h: "Bolivianos", align: "right", render: (r) => fmtMoney(r.bolivianos), sx: { fontWeight: 700, color: "#1565c0" } },
        { h: "Fecha", render: (r) => fmtFecha(r.fecha_registro) }, { h: "Observación", render: (r) => r.obs },
        columnaEditar(setEditando, SUB_COLORES[1]),
      ]}
    />
    {editando && (
      <EditarTraspasoDialog
        fila={editando} bancos={catalogos.bancos} onClose={() => setEditando(null)}
        onGuardado={() => { setEditando(null); setOk(`Traspaso N.º ${editando.id} actualizado.`); paginado.recargar(); }}
      />
    )}
    </>
  );
}
